import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { useEffect, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import { reportMemoryCanary } from '../../helpers/storageTelemetry';
import Button from '../../ui/Button';
import RemoveIcon from '../../assets/icons/close.svg';
import ButtonsGroup from '../../ui/ButtonsGroup';
import { STATIC_IMAGES } from '../../app.config.vars/staticImages';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../ui/Tooltip';
import InfoIcon from '../../assets/icons/info.svg';
import { useAppDispatch } from '../../store';
import { setIsModalOpen, setModalContent, setModalContentProps } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
import InPageCamera from '../InPageCamera';
const resolveCompressionOptions = (baseSizeMB) => {
    const deviceMemory = navigator.deviceMemory;
    if (typeof deviceMemory === 'number' && deviceMemory > 0) {
        if (deviceMemory <= 2) {
            return { maxSizeMB: Math.min(baseSizeMB, 1), useWebWorker: false };
        }
        if (deviceMemory <= 4) {
            return { maxSizeMB: Math.min(baseSizeMB, 1.5), useWebWorker: true };
        }
    }
    return { maxSizeMB: baseSizeMB, useWebWorker: true };
};
const MediaFileUploader = ({ filePreview, selectedFile, selectedFileMimeType, onChange, maxImageSizeMB, disabled, error, errorMessage, accept, buttonLabel, buttonVariant, useCamera, buttonIcon, previewStyles, label, additionalLabel, tooltipContent, maxFileSizeMB, imageCompressionSizeMB, }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const fileInputRef = useRef(null);
    const [fileType, setFileType] = useState(null);
    const [isCorrectMediaType, setIsCorrectMediaType] = useState(false);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    // Returns true if the file was accepted (compressed + propagated via onChange),
    // false if it was rejected for being too large.
    const processImageFile = async (file) => {
        if (maxImageSizeMB && file.size > maxImageSizeMB * 1024 * 1024) {
            onChange(null);
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.cannotUploadFile.key));
            dispatch(setModalContentProps({ maxSize: maxImageSizeMB }));
            return false;
        }
        reportMemoryCanary('pre-compression');
        const compressedFile = await imageCompression(file, resolveCompressionOptions(imageCompressionSizeMB));
        reportMemoryCanary('post-compression');
        onChange(compressedFile);
        return true;
    };
    const handleFileSelect = async (event) => {
        const input = event.currentTarget;
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        try {
            if (file.type.includes('image')) {
                const accepted = await processImageFile(file);
                if (!accepted) {
                    input.value = '';
                }
            }
            else {
                if (maxFileSizeMB && file.size > maxFileSizeMB * 1024 * 1024) {
                    onChange(null);
                    input.value = '';
                    dispatch(setIsModalOpen(true));
                    dispatch(setModalContent(MODAL_CONTENT.cannotUploadFile.key));
                    dispatch(setModalContentProps({ maxSize: maxFileSizeMB }));
                }
                else {
                    onChange(file);
                }
            }
        }
        catch (error) {
            console.error('Compression error:', error);
        }
    };
    const handleCameraCapture = async (file) => {
        setIsCameraOpen(false);
        try {
            await processImageFile(file);
        }
        catch (error) {
            console.error('Compression error:', error);
        }
    };
    const openCamera = () => {
        // Use the in-page camera (getUserMedia) instead of the system camera intent.
        // Launching the system camera backgrounds Chrome on Android, which can let
        // lowmemorykiller reap the renderer — that was the OOM-on-photo failure
        // mode observed in production. The in-page camera keeps the tab active.
        setIsCameraOpen(true);
    };
    const openGallery = () => {
        if (fileInputRef.current) {
            fileInputRef.current.setAttribute('accept', accept);
            fileInputRef.current.removeAttribute('capture');
            fileInputRef.current.click();
        }
    };
    const handleDelete = () => {
        fileInputRef.current.value = '';
        onChange(null);
    };
    useEffect(() => {
        // Prefer the explicit mimeType prop — it's the only reliable signal when
        // `selectedFile` is an object URL (blob:...). Fall back to the legacy
        // data-URL prefix sniff so admin-side callers that still pass base64 work.
        const detectFromMime = (mime) => {
            if (!mime)
                return null;
            if (mime.startsWith('image/'))
                return 'image';
            if (mime.startsWith('video/'))
                return 'video';
            if (mime.startsWith('audio/'))
                return 'audio';
            return 'unknown';
        };
        const detectFromDataUrl = (url) => {
            if (!url)
                return 'unknown';
            if (url.includes('data:image'))
                return 'image';
            if (url.includes('data:video'))
                return 'video';
            if (url.includes('data:audio'))
                return 'audio';
            return 'unknown';
        };
        const detected = detectFromMime(selectedFileMimeType) ?? detectFromDataUrl(selectedFile);
        setFileType(detected);
        setIsCorrectMediaType(detected !== 'unknown');
    }, [selectedFile, selectedFileMimeType]);
    return (_jsxs("div", { className: "file-uploader", children: [label && (_jsxs("div", { className: "label", children: [label, tooltipContent && (_jsx(Tooltip, { content: tooltipContent, position: "top", children: _jsx(InfoIcon, { style: {
                                width: '16px',
                                height: '16px',
                                marginLeft: '4px',
                                cursor: 'help',
                                display: 'inline-block',
                                verticalAlign: 'middle',
                            } }) })), !tooltipContent && (_jsx(Tooltip, { content: t('tooltip.file_upload'), position: "top", children: _jsx(InfoIcon, { style: {
                                width: '16px',
                                height: '16px',
                                marginLeft: '4px',
                                cursor: 'help',
                                display: 'inline-block',
                                verticalAlign: 'middle',
                            } }) }))] })), additionalLabel && _jsx("div", { className: "additional-label", children: additionalLabel }), _jsxs("div", { className: "file-uploader-wrapper", children: [_jsx("div", { className: "file-uploader-preview", style: previewStyles, children: selectedFile && isCorrectMediaType ? (fileType === 'image' ? (_jsxs(_Fragment, { children: [_jsx("img", { src: selectedFile, alt: "Preview", className: 'file-preview image' }), !disabled && (_jsx(Button, { shape: 'square', icon: _jsx(RemoveIcon, {}), iconSize: 'small', onClick: handleDelete, onKeyDown: (event) => {
                                        if (event.code === 'Space') {
                                            event.preventDefault();
                                            handleDelete();
                                        }
                                    } }))] })) : fileType === 'video' ? (_jsxs(_Fragment, { children: [_jsx("video", { src: selectedFile, className: 'file-preview video', controls: true }), !disabled && (_jsx(Button, { shape: 'square', icon: _jsx(RemoveIcon, {}), iconSize: 'small', onClick: handleDelete, onKeyDown: (event) => {
                                        if (event.code === 'Space') {
                                            event.preventDefault();
                                            handleDelete();
                                        }
                                    } }))] })) : (_jsxs(_Fragment, { children: [_jsx("audio", { src: selectedFile, className: 'file-preview audio', controls: true }), !disabled && (_jsx(Button, { shape: 'square', icon: _jsx(RemoveIcon, {}), iconSize: 'small', onClick: handleDelete, onKeyDown: (event) => {
                                        if (event.code === 'Space') {
                                            event.preventDefault();
                                            handleDelete();
                                        }
                                    } }))] }))) : filePreview ? (_jsx("img", { src: filePreview, alt: 'preview image', className: 'file-preview' })) : (_jsx("img", { src: localStorage.getItem(STATIC_IMAGES.imagePreview.key) || undefined, alt: 'image icon', className: 'image-preview-icon' })) }), error && errorMessage && _jsx("div", { className: "form-field-error-message", children: errorMessage }), !disabled && (_jsxs(_Fragment, { children: [_jsx("div", { className: "file-uploader-input", children: _jsx("input", { ref: fileInputRef, type: "file", accept: accept, onChange: handleFileSelect, tabIndex: -1 }) }), _jsx("div", { className: "file-uploader-controls", children: _jsxs(ButtonsGroup, { children: [useCamera && isMobile && (_jsx(Button, { onClick: openCamera, label: t('button.open_camera'), onKeyDown: (event) => {
                                                if (event.code === 'Space') {
                                                    event.preventDefault();
                                                    openCamera();
                                                }
                                            } })), _jsx(Button, { type: "button", onClick: openGallery, label: buttonLabel, variant: buttonVariant, icon: buttonIcon, manageIcon: 'fill', onKeyDown: (event) => {
                                                if (event.code === 'Space') {
                                                    event.preventDefault();
                                                    openGallery();
                                                }
                                            } })] }) })] }))] }), _jsx(InPageCamera, { open: isCameraOpen, onCapture: handleCameraCapture, onClose: () => setIsCameraOpen(false) })] }));
};
export default MediaFileUploader;
